import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import CustomAlert from '../../common/CustomAlert/CustomAlert';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getDefaultContentByNodeType,
  fetchWorkflows,
  extractHtmlContent,
  applyChanges,
  getNodeType,
} from '../services/createService';
import styles from '../styles/CreateTemplate.module.css';

import AddressTemplate from '../../CustomTemplate/AddressTemplate/AddressTemplate';
import DefaultTemplate from '../../CustomTemplate/Default/DefaultTemplate';
import DropdownButton from '../../common/components/Dropdown/Dropdown';
import {
  uploadImageToAWS,
  downloadImageFromAWS,
} from '../services/imageService';

import { saveWorkflow } from '../services/portalService';
import { base64Encode } from '../../../utils/base64Utils';
import SelfSchedulingNode from '../../CustomNodes/SelfScheduling/selfScheduling';

const CreateTemplate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, handleLogout } = useAuth();
  const {
    template,
    template_name,
    template_status,
    node_type,
    template_id,
    wfw_id,
    wfw_node_id,
    delete_status,
  } = location.state || {};
  const [htmlContent, setHtmlContent] = useState(template || '');
  const [userPrompt, setUserPrompt] = useState('');

  const [loading, setLoading] = useState(false);
  const [workflowId, setWorkflowId] = useState(wfw_id || '');
  const [nodeId, setNodeId] = useState(wfw_node_id || '');
  const [nodeType, setNodeType] = useState('');
  const [templateName, setTemplateName] = useState(template_name || '');
  const [workflows, setWorkflows] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [uploadImage, setUploadImage] = useState('');

  const [templateStatus, setTemplateStatus] = useState(
    template_status || 'draft'
  );
  const [dropdownLabel, setDropdownLabel] = useState('Draft');
  const [selected, setSelected] = useState('');

  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [nodes, setNodes] = useState([]);
  const [state, setState] = useState({
    heading: '',
    description: '',
    header1: '',
    header2: '',
    bannerDesc: '',
    bannerButton: '',
  });
  const containerRef = useRef(null);
  const options = ['Draft', 'Publish'];
  const [isLoading, setIsLoading] = useState(false);
  const [containerStyle, setContainerStyle] = useState({});
  const hasExtractedInitialStyles = useRef(false);

  // Helper function to convert CSS property names to React camelCase
  const cssToReactStyle = (cssProp) => {
    return cssProp.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
  };

  useEffect(() => {
    console.log('Template:', template, node_type);
    if (!template && node_type) {
      setHtmlContent(
        ReactDOMServer.renderToStaticMarkup(
          getDefaultContentByNodeType(node_type)
        )
      );
    }
  }, [template, node_type]);

  // Extract styles from PageContainer on initial load only
  useEffect(() => {
    if (htmlContent && !hasExtractedInitialStyles.current) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;

      // Look for PageContainer or wrapper elements with inline styles
      const containerSelectors = [
        '.selfSchedulingNode-wrapper',
        '.selfSchedulingNode-Container',
        '.chooseProductsNode-Container',
        '.checkForServiceNode-Container',
        '.accountSetupNode-Container',
        '.addOnsNode-Container',
        '.paymentNode-Container',
        '.successNode-Container',
        '[class*="Container"]',
        '[class*="wrapper"]',
      ];

      let wrapper = null;
      for (const selector of containerSelectors) {
        wrapper = tempDiv.querySelector(selector);
        if (wrapper && wrapper.style && wrapper.style.length > 0) {
          break;
        }
      }

      if (wrapper && wrapper.style && wrapper.style.length > 0) {
        const styleObj = {};
        for (let i = 0; i < wrapper.style.length; i++) {
          const prop = wrapper.style[i];
          const camelProp = cssToReactStyle(prop);
          styleObj[camelProp] = wrapper.style.getPropertyValue(prop);
        }
        setContainerStyle(styleObj);
        hasExtractedInitialStyles.current = true;
      }
    }
  }, [htmlContent]);

  useEffect(() => {
    if (!containerRef.current) return;

    // const elements = {
    //   heading: 'h2[class*="heading"]',
    //   description: 'p[class*="sub-heading"]',
    //   header1: 'h3[class*="package-title"]',
    //   header2: 'h1[class*="main-title"]',
    //   bannerDesc: 'p[class*="description"]',
    //   bannerButton: 'button[class*="shop-button"]',
    // };

    // const newState = Object.entries(elements).reduce((acc, [key, selector]) => {
    //   const element = containerRef.current.querySelector(selector);
    //   acc[key] = element?.innerHTML || '';
    //   return acc;
    // }, {});

    // setState((prev) => {
    //   if (JSON.stringify(prev) === JSON.stringify(newState)) {
    //     return prev;
    //   }
    //   return newState;
    // });
    setNodeType(getNodeType(node_type));
  }, [template, node_type, htmlContent]);

  useEffect(() => {
    fetchWorkflows(token, setWorkflows);
  }, [token]);

  useEffect(() => {
    if (workflowId) {
      const selectedWorkflow = workflows.find(
        (workflow) => String(workflow.id) === String(workflowId)
      );
      if (selectedWorkflow) {
        setNodes(selectedWorkflow.workflow.nodes);
      }
    }
  }, [workflowId, workflows]);

  const handleApplyChanges = async (payload) => {
    // Handle self-scheduling node data updates
    if (payload && typeof payload === 'object' && payload.startTime) {
      try {
        // Update the HTML content with the selected schedule data
        const updatedHtml = htmlContent.replace(
          /<div class="selfSchedulingNode-wrapper"[\s\S]*?<\/div>\s*<\/div>\s*<\/div>/,
          (match) => {
            // Inject the schedule data as data attributes or hidden fields
            return match.replace(
              '<div class="selfSchedulingNode-wrapper"',
              `<div class="selfSchedulingNode-wrapper" data-start-time="${payload.startTime}" data-end-time="${payload.endTime}" data-display-date="${payload.displayDate}"`
            );
          }
        );
        setHtmlContent(updatedHtml);
        setSnackbarMessage('Schedule selected successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Error updating schedule:', error);
        setSnackbarMessage('Failed to update schedule');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
      return;
    }

    // Handle AI prompt-based changes for other nodes
    setIsLoading(true);
    const prompt = `${userPrompt}`;
    try {
      const response = await applyChanges(htmlContent, prompt, token);
      if (response && response.data) {
        let extractedHtml = extractHtmlContent(response.data);

        // Extract styles from PageContainer for all node types
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = extractedHtml;

        // Look for PageContainer or wrapper elements with inline styles
        const containerSelectors = [
          '.selfSchedulingNode-wrapper',
          '.selfSchedulingNode-Container',
          '.chooseProductsNode-Container',
          '.checkForServiceNode-Container',
          '.accountSetupNode-Container',
          '.addOnsNode-Container',
          '.paymentNode-Container',
          '.successNode-Container',
          '[class*="Container"]',
          '[class*="wrapper"]',
        ];

        let wrapper = null;
        for (const selector of containerSelectors) {
          wrapper = tempDiv.querySelector(selector);
          if (wrapper && wrapper.style && wrapper.style.length > 0) {
            break;
          }
        }

        if (wrapper && wrapper.style && wrapper.style.length > 0) {
          const styleObj = {};
          // Extract all inline styles and convert to React camelCase
          for (let i = 0; i < wrapper.style.length; i++) {
            const prop = wrapper.style[i];
            const camelProp = cssToReactStyle(prop);
            styleObj[camelProp] = wrapper.style.getPropertyValue(prop);
          }

          // Remove from wrapper to avoid duplication
          wrapper.removeAttribute('style');
          extractedHtml = tempDiv.innerHTML;

          // Update both states together
          if (response.status === 200) {
            setHtmlContent(extractedHtml);
            setContainerStyle(styleObj);
            setSnackbarMessage('Changes applied successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
          }
        } else if (response.status === 200) {
          setHtmlContent(extractedHtml);
          setSnackbarMessage('Changes applied successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        }
      }
    } catch (error) {
      console.error('Error generating HTML:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSave = async (temp_status) => {
    setIsLoading(true);
    try {
      if (!templateName || !htmlContent) {
        setSnackbarMessage('Template name and content are required.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      if (!workflowId || !nodeId) {
        setSnackbarMessage('Workflow and Node must be provided.');
        return;
      }
      if (!htmlContent) {
        setSnackbarMessage(
          'Html code must be generated, try some prompt to generate.'
        );
        return;
      }

      let contentToSave = containerRef.current
        ? containerRef.current.innerHTML
        : htmlContent;

      // Inject containerStyle back into the PageContainer before saving (for all node types)
      if (Object.keys(containerStyle).length > 0) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = contentToSave;

        // Look for the appropriate container element
        const containerSelectors = [
          '.selfSchedulingNode-wrapper',
          '.selfSchedulingNode-Container',
          '.chooseProductsNode-Container',
          '.checkForServiceNode-Container',
          '.accountSetupNode-Container',
          '.addOnsNode-Container',
          '.paymentNode-Container',
          '.successNode-Container',
          '[class*="Container"]',
          '[class*="wrapper"]',
        ];

        let wrapper = null;
        for (const selector of containerSelectors) {
          wrapper = tempDiv.querySelector(selector);
          if (wrapper) break;
        }

        if (wrapper) {
          // Apply containerStyle to wrapper (convert camelCase back to CSS format)
          Object.keys(containerStyle).forEach((camelProp) => {
            const cssProp = camelProp.replace(/([A-Z])/g, '-$1').toLowerCase();
            wrapper.style.setProperty(cssProp, containerStyle[camelProp]);
          });
          contentToSave = tempDiv.innerHTML;
        }
      }

      if (containerRef.current) {
        setHtmlContent(contentToSave);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/portalbuilder/create`,
        {
          template_name: templateName,
          template: base64Encode(contentToSave),
          wfw_id: workflowId,
          wfw_node_id: nodeId,
          template_status: temp_status,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        }
      );
      const template = response.data;
      const replaceTemplateImage = (templateHtml, newImageSrc) => {
        const templateHtmldata = templateHtml.replace(
          /<img[^>]+src="([^"]+)"[^>]*>/,
          `<div style="width: 100%;">
             <img src="${newImageSrc}" alt="Updated Image" 
                 style="width: 100%;object-fit: cover;" className="landing-image">
           </div>`
        );
        // console.log(templateHtmldata,'templateHtmldata')

        return templateHtmldata;
      };
      let updatedTemplate;
      if (response.status === 201) {
        const result = await uploadImageToAWS(
          uploadImage,
          workflowId,
          nodeId,
          token,
          handleLogout
        );
        const imageData = await downloadImageFromAWS(
          workflowId,
          nodeId,
          token,
          handleLogout
        );
        if (imageData)
          if (!imageData)
            //console.log("Image Data:", imageData);
            console.error('Failed to download image');
        if (imageData) {
          updatedTemplate = replaceTemplateImage(template.template, imageData);
          const endpoint = `update/${template.id}`;
          const payload = {
            template_name: template.template_name,
            template: base64Encode(updatedTemplate || contentToSave),
          };
          const response = await saveWorkflow(
            template.id,
            endpoint,
            token,
            payload
          );
          if (response && response.status === 200) {
            console.log('Image updated successfully in template');
            setIsLoading(false);
          } else {
            console.error(
              `Failed to update template image. Status: ${response.status}`
            );
            setIsLoading(false);
          }
        }
      }
      let msg_stat = 'saved';
      if ('publish' == temp_status) {
        setIsLoading(true);
        msg_stat = 'Template published successfully!';
      } else if ('draft' == temp_status) {
        msg_stat = 'drafted';
      } else if ('saved' == temp_status) {
        msg_stat = 'Template ' + msg_stat + ' successfully!';
      }
      setSnackbarMessage(msg_stat);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setTimeout(() => {
        setIsLoading(false);
        navigate(`/portalBuilder/${template.id}`, {
          state: {
            // template: updatedTemplate ? updatedTemplate : template.template,
            template: template.template,
            template_id: template.id,
            template_name: template.template_name,
            template_json: template.template_json,
            wfw_id: template.wfw_id,
            wfw_node_id: template.wfw_node_id,
            node_type: node_type,
          },
        });
        if ('publish' == temp_status) {
          navigate(`/workflow/${wfw_id}`, { state: { workflowId: wfw_id } });
        }
      }, 4000);
    } catch (error) {
      if (error.response && error.response.status === 409) {
        setSnackbarMessage(
          'Template with the same workflow and node ID already exists.'
        );
      } else {
        console.error('Error saving template:', error);
      }
    }
  };

  const getNodeTemplate = () => {
    console.log('wfw_id : ', wfw_id);

    switch (node_type) {
      case 'addressNode1':
        return (
          <AddressTemplate
            userPrompt={userPrompt}
            onUserPromptChange={handleUserPromptChange}
            onhandleInputChange={handleInputChange}
            onApplyChanges={handleApplyChanges}
            heading={state.heading}
            description={state.description}
            header1={state.header1}
            header2={state.header2}
            bannerDesc={state.bannerDesc}
            bannerButton={state.bannerButton}
            workflowId={wfw_id}
            onImageUpload={handleImageUploadChange}
          />
        );

      case 'selfSchedulingNode':
        return (
          <DefaultTemplate
            userPrompt={userPrompt}
            onUserPromptChange={handleUserPromptChange}
            onApplyChanges={handleApplyChanges}
            nodeType={nodeType}
            workflowId={wfw_id}
          />
        );

      default:
        return (
          <DefaultTemplate
            userPrompt={userPrompt}
            onUserPromptChange={handleUserPromptChange}
            onApplyChanges={handleApplyChanges}
            nodeType={nodeType}
            workflowId={wfw_id}
          />
        );
    }
  };

  const handleInputChange = (key, value) => {
    setState((prev) => {
      const newState = { ...prev, [key]: value };

      if (containerRef.current) {
        const elementSelectors = {
          heading: 'h2[class*="heading"]',
          description: 'p[class*="sub-heading"]',
          header1: 'h3[class*="package-title"]',
          header2: 'h1[class*="main-title"]',
          bannerDesc: 'p[class*="description"]',
          bannerButton: 'button[class*="shop-button"]',
        };

        const selector = elementSelectors[key];
        if (selector) {
          const element = containerRef.current.querySelector(selector);
          if (element) {
            element.innerText = newState[key];
          }
        }
      }

      return newState;
    });
  };

  const handleUserPromptChange = (value) => {
    setUserPrompt(value);
  };

  /*const handleImageUploadChange = (value) => {
    setUploadImage(value);
  };*/

  const handleImageUploadChange = (file) => {
    //This is tempory image
    if (!file) return;

    const imageUrl = URL.createObjectURL(file);
    setUploadImage(file);

    // const updatedHtml = htmlContent.replace(
    //   /<img\s+[^>]*src="([^"]+)"[^>]*>/,
    //   `<img src="${imageUrl}" alt="image" style="width: 100%; height: 85vh; min-height: 500px; object-fit: cover;" class="landing-image"/>`
    // );
    const updatedHtml = htmlContent.replace(
      /<img\s+[^>]*src="([^"]+)"[^>]*>/,
      `
  <style>
    img[src*="${imageUrl.split('/').pop()}"] {
      height: 40px !important;
      width: auto !important;
      max-width: 100% !important;
      object-fit: contain !important;
      display: block !important;
    }
  </style>
  <img src="${imageUrl}" alt="image" />
  `
    );

    setHtmlContent(updatedHtml);
    console.log(updatedHtml, 'Updated htmlContent');
  };

  return (
    <>
      {isLoading && (
        <div className="loader-overlay">
          <div className="loader"></div>
        </div>
      )}
      <div className={styles.container}>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbarSeverity}>{snackbarMessage}</Alert>
        </Snackbar>
        <div className={styles.sidebar}>
          <h3 className={styles.sidebarH3}>
            <input
              type="text"
              placeholder="Template Name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </h3>
          {false && (
            <div className={styles.marginBottom20}>
              <label className={styles.componentLabel}>Workflow:</label>
              <select
                value={workflowId}
                onChange={(e) => setWorkflowId(e.target.value)}
                className={styles.selectStyle}
                disabled={Boolean(1)}
              >
                <option value="">Select a Workflow</option>
                {workflows.map((workflow) => (
                  <option key={workflow.id} value={workflow.id}>
                    {workflow.workflow_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {getNodeTemplate()}

          <div className={styles.buttonContainer}>
            <div className={styles.buttonGroup}>
              {[
                {
                  label: 'Save as Draft',
                  onClick: () => handleSave('saved'),
                  className: styles.buttonSave,
                },
                {
                  label: 'Publish',
                  onClick: () => handleSave('publish'),
                  className: styles.buttonSave,
                },
              ].map((btn, index) => (
                <button
                  key={index}
                  onClick={btn.onClick}
                  className={btn.className}
                >
                  {btn.label}
                </button>
              ))}
            </div>
            {/* <div style={{ position: "relative", paddingLeft: 10 }}>
            <DropdownButton
              label={dropdownLabel}
              options={options}
              onSelect={handleSelect}
              direction="upward"
              style={{ marginBottom: "00px" }}
            />
          </div> */}
          </div>
        </div>
        <div className={styles.content} style={containerStyle}>
          {loading && <div className={styles.loader}></div>}

          {node_type === 'selfSchedulingNode' ? (
            <div className={styles.preview} ref={containerRef}>
              <SelfSchedulingNode
                htmlContent={htmlContent}
                onApplyChanges={handleApplyChanges}
                setHtmlContent={setHtmlContent}
                token={token}
              />
            </div>
          ) : (
            <div
              className={styles.preview}
              ref={containerRef}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default CreateTemplate;
